import { useCallback, useEffect, useRef, useState } from 'react';
import { useGoogleMapsLoader } from './useGoogleMapsLoader';

export const useGooglePlaces = (apiKey) => {
  const { isLoaded } = useGoogleMapsLoader(apiKey, ['places']);
  const [autocomplete, setAutocomplete] = useState(null);
  const inputRef = useRef(null);
  const lastInputRef = useRef(null);

  const initializeAutocomplete = useCallback(() => {
    if (!apiKey || !isLoaded) return null;
    if (!inputRef.current) return null;
    if (!window.google || !window.google.maps || !window.google.maps.places) return null;
    if (lastInputRef.current === inputRef.current && autocomplete) {
      return autocomplete;
    }
    const autocompleteInstance = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions: { country: 'cl' }, // Restrict to Chile
      fields: ['address_components', 'geometry', 'name', 'formatted_address']
    });
    lastInputRef.current = inputRef.current;
    setAutocomplete(autocompleteInstance);
    return autocompleteInstance;
  }, [apiKey, isLoaded, autocomplete]);

  useEffect(() => {
    if (!apiKey || !isLoaded) return;
    if (initializeAutocomplete()) return;

    const interval = setInterval(() => {
      if (initializeAutocomplete()) {
        clearInterval(interval);
      }
    }, 200);

    const timeout = setTimeout(() => clearInterval(interval), 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [apiKey, isLoaded, initializeAutocomplete]);

  const setupPlaceChangedListener = useCallback((callback, instance = null) => {
    const targetAutocomplete = instance || autocomplete || initializeAutocomplete();
    if (!targetAutocomplete) return null;
    const listener = targetAutocomplete.addListener('place_changed', () => {
      const place = targetAutocomplete.getPlace();

      if (place.geometry) {
        const addressComponents = place.address_components || [];
        const location = {
          formatted_address: place.formatted_address,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          components: {}
        };

        // Parse address components
        addressComponents.forEach(component => {
          const type = component.types[0];
          switch (type) {
            case 'street_number':
              location.components.street_number = component.long_name;
              break;
            case 'route':
              location.components.street_name = component.long_name;
              break;
            case 'locality':
              location.components.city = component.long_name;
              break;
            case 'administrative_area_level_3':
              location.components.comuna = component.long_name;
              break;
            case 'sublocality':
            case 'sublocality_level_1':
              if (!location.components.comuna) {
                location.components.comuna = component.long_name;
              }
              break;
            case 'administrative_area_level_1':
              location.components.state = component.long_name;
              break;
            case 'administrative_area_level_2':
              location.components.region = component.long_name;
              break;
            case 'country':
              location.components.country = component.long_name;
              break;
            case 'postal_code':
              location.components.postal_code = component.long_name;
              break;
          }
        });

        callback(location);
      }
    });
    return listener;
  }, [autocomplete, initializeAutocomplete]);

  return {
    isLoaded,
    inputRef,
    setupPlaceChangedListener,
    initializeAutocomplete
  };
};